import * as THREE from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
const logger = console
import type { GlbLoadResult } from './glb'
import {
  composeImportedModelCacheKey,
  type ImportedModelPublishScopeKey,
} from './cache-scope'
import { IMPORTED_MODEL_RUNTIME_CACHE_REVISION } from './runtime-cache-revision'
import { disposeImportedModelTopologyHosts } from '../topology/imported-model-topology-runtime'
import type { AssemblyManifest, AssemblyModelAsset, PartAsset } from './types'

export type AssemblyManifestCacheKey = `${string}:${string}`

export interface CachedAssemblyManifest {
  manifest: AssemblyManifest
  warnings: string[]
  cacheRevision?: string
  /** 无 external_file_uuid 但含内嵌几何的 manifest 节点源 Object3D 引用 */
  inlinePartSourceByNodeId?: Record<string, import('three').Object3D>
  /** 无 external_file_uuid 且语义为 assembly 的 manifest 节点源 Object3D 引用 */
  inlineAssemblySourceByNodeId?: Record<string, import('three').Object3D>
}

export type ImportedModelCacheListener = () => void

type CacheEntry = {
  result: GlbLoadResult
  refCount: number
  lastUsedAt: number
}

export type GlbResourceCacheOptions = {
  maxEntries?: number
}

const PARSED_GLB_CACHE_VERSION = 1

/** 普通 GLB 完整 scene 解析使用的默认缓存变体。 */
export const DEFAULT_PARSED_GLB_CACHE_VARIANT = 'full-scene:v1'

export type ParsedGlbCacheKey = string

function buildParsedGlbKey(
  fileUuid: string,
  version: string,
  variant: string,
): ParsedGlbCacheKey {
  return JSON.stringify([fileUuid, version, variant])
}

function buildKey(fileUuid: string, version: string): AssemblyManifestCacheKey {
  return `${fileUuid}:${version}`
}

class ObservableImportedModelCache {
  private readonly listeners = new Set<ImportedModelCacheListener>()
  private revision = 0

  subscribe(listener: ImportedModelCacheListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getRevision(): number {
    return this.revision
  }

  protected notifyChange(): void {
    this.revision += 1
    this.listeners.forEach((listener) => listener())
  }
}

export class AssemblyManifestCache {
  private readonly entries = new Map<AssemblyManifestCacheKey, CachedAssemblyManifest>()
  private hits = 0
  private misses = 0

  get(fileUuid: string, version: string): CachedAssemblyManifest | undefined {
    const key = buildKey(fileUuid, version)
    const entry = this.entries.get(key)
    if (entry?.cacheRevision === IMPORTED_MODEL_RUNTIME_CACHE_REVISION) {
      this.hits += 1
      return entry
    }
    if (entry) {
      this.entries.delete(key)
    }
    this.misses += 1
    return undefined
  }

  set(fileUuid: string, version: string, entry: CachedAssemblyManifest): void {
    this.entries.set(buildKey(fileUuid, version), {
      ...entry,
      cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    })
  }

  delete(fileUuid: string, version: string): void {
    this.entries.delete(buildKey(fileUuid, version))
  }

  clear(): void {
    this.entries.clear()
  }

  stats(): { size: number; hits: number; misses: number } {
    return {
      size: this.entries.size,
      hits: this.hits,
      misses: this.misses,
    }
  }
}

export type AssemblyModelCacheKey = `${string}:${string}:${string}`

function buildAssemblyModelKey(
  fileUuid: string,
  version: string,
  publishScopeKey: ImportedModelPublishScopeKey | undefined,
): AssemblyModelCacheKey {
  return composeImportedModelCacheKey(fileUuid, version, publishScopeKey)
}

export class AssemblyModelCache extends ObservableImportedModelCache {
  private readonly entries = new Map<AssemblyModelCacheKey, AssemblyModelAsset>()
  private hits = 0
  private misses = 0

  private readEntry(
    fileUuid: string,
    version: string,
    publishScopeKey: ImportedModelPublishScopeKey | undefined,
    trackStats: boolean,
  ): AssemblyModelAsset | undefined {
    const key = buildAssemblyModelKey(fileUuid, version, publishScopeKey)
    const entry = this.entries.get(key)
    if (entry?.cacheRevision === IMPORTED_MODEL_RUNTIME_CACHE_REVISION) {
      if (trackStats) {
        this.hits += 1
      }
      return entry
    }
    if (entry) {
      disposeAssemblyModelAssetResources(entry)
      this.entries.delete(key)
      this.notifyChange()
    }
    if (trackStats) {
      this.misses += 1
    }
    return undefined
  }

  get(
    fileUuid: string,
    version: string,
    publishScopeKey?: ImportedModelPublishScopeKey,
  ): AssemblyModelAsset | undefined {
    return this.readEntry(fileUuid, version, publishScopeKey, true)
  }

  peek(
    fileUuid: string,
    version: string,
    publishScopeKey?: ImportedModelPublishScopeKey,
  ): AssemblyModelAsset | undefined {
    return this.readEntry(fileUuid, version, publishScopeKey, false)
  }

  set(asset: AssemblyModelAsset, publishScopeKey?: ImportedModelPublishScopeKey): void {
    const key = buildAssemblyModelKey(asset.fileUuid, asset.version, publishScopeKey)
    const previous = this.entries.get(key)
    if (previous) {
      disposeAssemblyModelAssetResources(previous, asset.sceneTemplate)
    }
    this.entries.set(key, {
      ...asset,
      cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    })
    this.notifyChange()
  }

  delete(
    fileUuid: string,
    version: string,
    publishScopeKey?: ImportedModelPublishScopeKey,
  ): void {
    const key = buildAssemblyModelKey(fileUuid, version, publishScopeKey)
    const entry = this.entries.get(key)
    if (!entry) {
      return
    }
    disposeAssemblyModelAssetResources(entry)
    this.entries.delete(key)
    this.notifyChange()
  }

  clear(): void {
    if (this.entries.size === 0) {
      return
    }
    this.entries.forEach((asset) => disposeAssemblyModelAssetResources(asset))
    this.entries.clear()
    this.notifyChange()
  }

  stats(): { size: number; hits: number; misses: number } {
    return {
      size: this.entries.size,
      hits: this.hits,
      misses: this.misses,
    }
  }
}

export type PartAssetCacheKey = `${string}:${string}:${string}`

function buildPartAssetKey(
  fileUuid: string,
  version: string,
  publishScopeKey: ImportedModelPublishScopeKey | undefined,
): PartAssetCacheKey {
  return composeImportedModelCacheKey(fileUuid, version, publishScopeKey)
}

interface Object3DResourceSnapshot {
  geometries: Set<THREE.BufferGeometry>
  materials: Set<THREE.Material>
  textures: Set<THREE.Texture>
}

function createEmptyObject3DResourceSnapshot(): Object3DResourceSnapshot {
  return {
    geometries: new Set<THREE.BufferGeometry>(),
    materials: new Set<THREE.Material>(),
    textures: new Set<THREE.Texture>(),
  }
}

function collectMaterialTextures(
  material: THREE.Material,
  textures: Set<THREE.Texture>,
): void {
  const materialLike = material as THREE.Material & Record<string, unknown>
  Object.values(materialLike).forEach((value) => {
    if (value instanceof THREE.Texture) {
      textures.add(value)
    }
  })
}

function collectObject3DResources(root: THREE.Object3D | undefined): Object3DResourceSnapshot {
  const snapshot = createEmptyObject3DResourceSnapshot()
  if (!root) {
    return snapshot
  }
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return
    }
    if (child.geometry) {
      snapshot.geometries.add(child.geometry)
    }
    const materials = Array.isArray(child.material) ? child.material : [child.material]
    materials.forEach((material) => {
      if (!material) {
        return
      }
      snapshot.materials.add(material)
      collectMaterialTextures(material, snapshot.textures)
    })
  })
  return snapshot
}

export function disposeMaterialTextures(
  material: THREE.Material,
  preservedTextures?: ReadonlySet<THREE.Texture>,
  disposedTextures?: Set<THREE.Texture>,
): void {
  const materialLike = material as THREE.Material & Record<string, unknown>
  Object.values(materialLike).forEach((value) => {
    if (
      value instanceof THREE.Texture
      && !preservedTextures?.has(value)
      && !disposedTextures?.has(value)
    ) {
      disposedTextures?.add(value)
      value.dispose()
    }
  })
}

function disposeObject3DResources(
  root: THREE.Object3D | undefined,
  preservedResources?: Object3DResourceSnapshot,
): void {
  if (!root) {
    return
  }
  const disposedResources = createEmptyObject3DResourceSnapshot()
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return
    }
    if (
      child.geometry
      && !preservedResources?.geometries.has(child.geometry)
      && !disposedResources.geometries.has(child.geometry)
    ) {
      disposedResources.geometries.add(child.geometry)
      child.geometry.dispose()
    }
    const materials = Array.isArray(child.material) ? child.material : [child.material]
    materials.forEach((material) => {
      if (!material) {
        return
      }
      if (
        !preservedResources?.materials.has(material)
        && !disposedResources.materials.has(material)
      ) {
        disposedResources.materials.add(material)
        disposeMaterialTextures(
          material,
          preservedResources?.textures,
          disposedResources.textures,
        )
        material.dispose()
      }
    })
  })
}

export function disposeAssemblyModelAssetResources(
  asset: AssemblyModelAsset,
  preserveSharedSceneTemplate?: THREE.Object3D,
): void {
  const preservedResources = preserveSharedSceneTemplate
    ? collectObject3DResources(preserveSharedSceneTemplate)
    : undefined
  disposeImportedModelTopologyHosts(asset.sceneTemplate)
  disposeObject3DResources(asset.sceneTemplate, preservedResources)
}

export function disposePartAsset(
  asset: PartAsset,
  preserveSharedTemplate?: THREE.Object3D,
): void {
  const preservedResources = preserveSharedTemplate
    ? collectObject3DResources(preserveSharedTemplate)
    : undefined
  disposeImportedModelTopologyHosts(asset.renderTemplate)
  disposeObject3DResources(asset.renderTemplate, preservedResources)
}

export class PartAssetCache extends ObservableImportedModelCache {
  private readonly entries = new Map<PartAssetCacheKey, PartAsset>()
  private hits = 0
  private misses = 0

  private readEntry(
    fileUuid: string,
    version: string,
    publishScopeKey: ImportedModelPublishScopeKey | undefined,
    trackStats: boolean,
  ): PartAsset | undefined {
    const key = buildPartAssetKey(fileUuid, version, publishScopeKey)
    const entry = this.entries.get(key)
    if (entry?.cacheRevision === IMPORTED_MODEL_RUNTIME_CACHE_REVISION) {
      if (trackStats) {
        this.hits += 1
      }
      return entry
    }
    if (entry) {
      disposePartAsset(entry)
      this.entries.delete(key)
      this.notifyChange()
    }
    if (trackStats) {
      this.misses += 1
    }
    return undefined
  }

  get(
    fileUuid: string,
    version: string,
    publishScopeKey?: ImportedModelPublishScopeKey,
  ): PartAsset | undefined {
    return this.readEntry(fileUuid, version, publishScopeKey, true)
  }

  peek(
    fileUuid: string,
    version: string,
    publishScopeKey?: ImportedModelPublishScopeKey,
  ): PartAsset | undefined {
    return this.readEntry(fileUuid, version, publishScopeKey, false)
  }

  set(asset: PartAsset, publishScopeKey?: ImportedModelPublishScopeKey): void {
    const key = buildPartAssetKey(asset.fileUuid, asset.version, publishScopeKey)
    const previous = this.entries.get(key)
    if (previous) {
      disposePartAsset(previous, asset.renderTemplate)
    }
    this.entries.set(key, {
      ...asset,
      cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    })
    this.notifyChange()
  }

  delete(
    fileUuid: string,
    version: string,
    publishScopeKey?: ImportedModelPublishScopeKey,
  ): void {
    const key = buildPartAssetKey(fileUuid, version, publishScopeKey)
    const entry = this.entries.get(key)
    if (!entry) {
      return
    }
    disposePartAsset(entry)
    this.entries.delete(key)
    this.notifyChange()
  }

  clear(): void {
    if (this.entries.size === 0) {
      return
    }
    this.entries.forEach((asset) => disposePartAsset(asset))
    this.entries.clear()
    this.notifyChange()
  }

  stats(): { size: number; hits: number; misses: number } {
    return {
      size: this.entries.size,
      hits: this.hits,
      misses: this.misses,
    }
  }
}

type ParsedGlbCacheEntry = {
  gltf: GLTF
  cacheVersion: number
}

export class ParsedGlbCache {
  private readonly entries = new Map<ParsedGlbCacheKey, ParsedGlbCacheEntry>()
  private hits = 0
  private misses = 0

  get(
    fileUuid: string,
    version: string,
    variant: string = DEFAULT_PARSED_GLB_CACHE_VARIANT,
  ): GLTF | undefined {
    const key = buildParsedGlbKey(fileUuid, version, variant)
    const entry = this.entries.get(key)
    if (entry?.cacheVersion === PARSED_GLB_CACHE_VERSION) {
      this.hits += 1
      return entry.gltf
    }
    if (entry) {
      this.entries.delete(key)
    }
    this.misses += 1
    return undefined
  }

  set(
    fileUuid: string,
    version: string,
    gltf: GLTF,
    variant: string = DEFAULT_PARSED_GLB_CACHE_VARIANT,
  ): void {
    this.entries.set(buildParsedGlbKey(fileUuid, version, variant), {
      gltf,
      cacheVersion: PARSED_GLB_CACHE_VERSION,
    })
  }

  delete(
    fileUuid: string,
    version: string,
    variant: string = DEFAULT_PARSED_GLB_CACHE_VARIANT,
  ): void {
    this.entries.delete(buildParsedGlbKey(fileUuid, version, variant))
  }

  clear(): void {
    this.entries.clear()
  }

  stats(): { size: number; hits: number; misses: number } {
    return {
      size: this.entries.size,
      hits: this.hits,
      misses: this.misses,
    }
  }
}

export class GlbResourceCache {
  private readonly entries = new Map<string, CacheEntry>()
  private readonly maxEntries: number

  constructor(options: GlbResourceCacheOptions = {}) {
    this.maxEntries = Math.max(options.maxEntries ?? 6, 1)
  }

  get(fileId: string): GlbLoadResult | undefined {
    const entry = this.entries.get(fileId)
    if (!entry) {
      return undefined
    }
    entry.lastUsedAt = Date.now()
    return entry.result
  }

  set(fileId: string, result: GlbLoadResult): void {
    const now = Date.now()
    const existing = this.entries.get(fileId)
    if (existing) {
      if (existing.result !== result && existing.refCount === 0) {
        disposeGlbResult(existing.result)
      }
      this.entries.set(fileId, {
        result,
        refCount: existing.refCount,
        lastUsedAt: now,
      })
    } else {
      this.entries.set(fileId, {
        result,
        refCount: 0,
        lastUsedAt: now,
      })
    }
    this.evictIfNeeded()
  }

  retain(fileId: string): void {
    const entry = this.entries.get(fileId)
    if (!entry) {
      return
    }
    entry.refCount += 1
    entry.lastUsedAt = Date.now()
  }

  release(fileId: string): void {
    const entry = this.entries.get(fileId)
    if (!entry) {
      return
    }
    entry.refCount = Math.max(0, entry.refCount - 1)
    entry.lastUsedAt = Date.now()
    this.evictIfNeeded()
  }

  clear(): void {
    for (const entry of this.entries.values()) {
      disposeGlbResult(entry.result)
    }
    this.entries.clear()
  }

  size(): number {
    return this.entries.size
  }

  private evictIfNeeded(): void {
    if (this.entries.size <= this.maxEntries) {
      return
    }

    const candidates = Array.from(this.entries.entries())
      .filter(([, entry]) => entry.refCount === 0)
      .sort((a, b) => a[1].lastUsedAt - b[1].lastUsedAt)

    for (const [fileId, entry] of candidates) {
      if (this.entries.size <= this.maxEntries) {
        break
      }
      disposeGlbResult(entry.result)
      this.entries.delete(fileId)
      logger.info(`[glb-cache] 已淘汰缓存资源: ${fileId}`)
    }
  }
}

export function disposeGlbResult(result: GlbLoadResult): void {
  result.object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return
    }
    child.geometry?.dispose()

    const materials = Array.isArray(child.material) ? child.material : [child.material]
    materials.forEach((material) => {
      if (!material) {
        return
      }
      disposeMaterialTextures(material)
      material.dispose()
    })
  })
}
